import { useMemo, useCallback } from 'react';
import { Transaction } from '@/types/transaction';
import { useTransaction } from '@/hooks/useTransaction';
import { useDashboardAnalytics } from './useDashboardAnalytics';
import { fromDateString } from '@/lib/utils/dateUtils';

export interface DashboardMetrics {
  totalIncome: number;
  totalExpense: number;
  currentBalance: number;
  pendingIncome: number;
  pendingExpense: number;
  projectedBalance: number;
}

export interface DashboardData {
  metrics: DashboardMetrics;
  analytics: ReturnType<typeof useDashboardAnalytics>;
  recentTransactions: Transaction[];
  incomeTransactions: Transaction[];
  expenseTransactions: Transaction[];
  isLoading: boolean;
}

export function useDashboardData(transactions: Transaction[] | undefined, isLoading: boolean, pageSize: number = 10): DashboardData {
  // Analytics especializado
  const analytics = useDashboardAnalytics(transactions);

  // Métricas financeiras memoizadas
  const metrics = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {
        totalIncome: 0,
        totalExpense: 0,
        currentBalance: 0,
        pendingIncome: 0,
        pendingExpense: 0,
        projectedBalance: 0
      };
    }

    const paidIncome = transactions
      .filter(t => t.type === "income" && t.is_paid)
      .reduce((acc, t) => acc + Number(t.amount), 0);

    const paidExpense = transactions
      .filter(t => t.type === "expense" && t.is_paid)
      .reduce((acc, t) => acc + Number(t.amount), 0);

    const pendingIncome = transactions
      .filter(t => t.type === "income" && !t.is_paid)
      .reduce((acc, t) => acc + Number(t.amount), 0);

    const pendingExpense = transactions
      .filter(t => t.type === "expense" && !t.is_paid)
      .reduce((acc, t) => acc + Number(t.amount), 0);

    return {
      totalIncome: paidIncome + pendingIncome,
      totalExpense: paidExpense + pendingExpense,
      currentBalance: paidIncome - paidExpense,
      pendingIncome,
      pendingExpense,
      projectedBalance: (paidIncome + pendingIncome) - (paidExpense + pendingExpense)
    };
  }, [transactions]);

  // Transações paginadas para performance - memoizadas
  const recentTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    return transactions
      .sort((a, b) => fromDateString(b.date).getTime() - fromDateString(a.date).getTime())
      .slice(0, pageSize);
  }, [transactions, pageSize]);

  const incomeTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    return transactions
      .filter(t => t.type === 'income')
      .sort((a, b) => fromDateString(b.date).getTime() - fromDateString(a.date).getTime());
  }, [transactions]);

  const expenseTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    return transactions
      .filter(t => t.type === 'expense')
      .sort((a, b) => fromDateString(b.date).getTime() - fromDateString(a.date).getTime());
  }, [transactions]);

  return {
    metrics,
    analytics,
    recentTransactions,
    incomeTransactions,
    expenseTransactions,
    isLoading
  };
}

// Hook para manipulações de transações no dashboard
export function useDashboardActions() {
  const { create, update, remove, togglePaid } = useTransaction();

  const handleEditTransaction = useCallback((transactionId: string, setEditingId: (id: string | null) => void, setDialogOpen: (open: boolean) => void) => {
    setEditingId(transactionId);
    setDialogOpen(true);
  }, []);

  const handleDeleteTransaction = useCallback(async (transactionId: string) => {
    await remove.mutateAsync(transactionId);
  }, [remove]);

  const handleTogglePaid = useCallback(async (transactionId: string) => {
    await togglePaid.mutateAsync(transactionId);
  }, [togglePaid]);

  const handleSaveTransaction = useCallback(async (data: any, editingId: string | null, handleClose: () => void) => {
    
    const transformedData = {
      type: data.type.toLowerCase() as "income" | "expense",
      amount: data.amount,
      description: data.description,
      category_id: data.categoryId,
      payment_account_id: data.accountId || "none",
      cost_center_id: data.costCenterId,
      date: fromDateString(data.date),
      is_paid: data.status === "PAID",
      notes: data.notes || "",
      is_recurring: data.isRecurring,
      // Use the mapped frequency from UnifiedTransactionForm instead of hardcoded MONTHLY
      recurrence_frequency: data.isRecurring ? data.recurrence_frequency : null,
      recurrence_end_date: data.recurrence_end_date ? fromDateString(data.recurrence_end_date) : undefined,
      // Use total_installments from data (mapped from recurrence count)
      total_installments: data.total_installments,
      payment_method: data.paymentMethod || "",
      payment_date: data.status === "PAID" ? fromDateString(data.date) : undefined,
      supplier: data.type === "EXPENSE" ? data.contactId || "" : "",
      client: data.type === "INCOME" ? data.contactId || "" : "",
      attachments: data.attachments.map((att: any) => ({
        file: att.file,
        description: att.description
      }))
    };

    
    if (editingId) {
      await update.mutateAsync({
        id: editingId,
        formData: transformedData
      });
    } else {
      await create.mutateAsync(transformedData);
    }

    handleClose();
  }, [create, update]);

  return {
    handleEditTransaction,
    handleDeleteTransaction,
    handleTogglePaid,
    handleSaveTransaction
  };
}